import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { AmplifierModalPortal } from './AmplifierModalPortal';
import { AmplifierData, AmplifierProjectData, AmplifierControlData, Vgs, BandParam } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { resolveAntennaDisplayName } from './antennaConfig';
import { createEmptyBandParam } from './remoteControlMapper';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import type { AmplifierLocaleKey } from '../../i18n/amplifier/locales';

const BAND_OPTIONS = Array.from({ length: 17 }, (_, index) => `BAND${index + 1}`);

const bandKeyFromIndex = (index: number): keyof BandParam => `vgs${index + 1}` as keyof BandParam;

type ParamFieldItem = {
  labelKey: AmplifierLocaleKey;
  field: keyof AmplifierProjectData;
  unit: string;
  fieldKey?: string;
};

/** 校准系数分页 — 右列 */
const CALIBRATION_PARAM_RIGHT: Array<{
  labelKey: AmplifierLocaleKey;
  field: keyof AmplifierProjectData;
  unit: string;
}> = [
  { labelKey: '电压校准系数', field: 'voltageCalibrationCoefficient', unit: '' },
  { labelKey: '电流校准系数', field: 'currentCalibrationCoefficient', unit: '' },
  { labelKey: '频率校准系数', field: 'frequencyMeasurementCalibrationCoefficient', unit: '' },
  { labelKey: 'VGS校准系数', field: 'vgsCalibrationCoefficient', unit: '' },
  { labelKey: 'DAC参考电压', field: 'exdacRefvCalibrationMv', unit: 'mV' },
];

/** 校准系数分页 — 左列（当前频段 bandParam 子字段） */
const CALIBRATION_BAND_FIELDS: Array<{
  labelKey: AmplifierLocaleKey;
  subKey: keyof Vgs;
  unit: string;
  step: number;
}> = [
  { labelKey: '偏置电压', subKey: 'vgsPower', unit: '', step: 1 },
  { labelKey: '截止频率', subKey: 'frequency', unit: '', step: 1 },
  { labelKey: '输入正向功率校准系数', subKey: 'inputPositive', unit: '', step: 1 },
  { labelKey: '输入反向功率校准系数', subKey: 'inputNegative', unit: '', step: 1 },
  { labelKey: '输出正向功率校准系数', subKey: 'outputPositive', unit: '', step: 1 },
  { labelKey: '输出反向功率校准系数', subKey: 'outputNegative', unit: '', step: 1 },
  { labelKey: '功放输入功率校准', subKey: 'ampInputPowerCmp', unit: '', step: 1 },
  { labelKey: '功放输出功率校准', subKey: 'ampOutputPowerCmp', unit: '', step: 1 },
];

/** 其他分页 — 左列 */
const OTHER_PARAM_LEFT: ParamFieldItem[] = [
  { labelKey: '保护电压', field: 'protectionVoltage', unit: 'V' },
  { labelKey: '保护电流', field: 'protectionCurrent', unit: 'A' },
  { labelKey: '保护输入正向功率', field: 'protectionInputPowerPositive', unit: 'W' },
  { labelKey: '保护输入反向功率', field: 'protectionInputPowerNegative', unit: 'W' },
  { labelKey: '保护输出正向功率', field: 'protectionOutputPowerPositive', unit: 'W' },
  { labelKey: '保护输出反向功率', field: 'protectionOutputPowerNegative', unit: 'W' },
];

/** 其他分页 — 右列 */
const OTHER_PARAM_RIGHT: ParamFieldItem[] = [
  { labelKey: '输入保护驻波比', field: 'protectionRf', unit: '', fieldKey: 'protectionRf-in' },
  { labelKey: '输出保护驻波比', field: 'protectionRf', unit: '', fieldKey: 'protectionRf-out' },
  { labelKey: '保护频率误差', field: 'protectionFrequencyError', unit: '' },
  { labelKey: '保护温度', field: 'protectionTemperature', unit: '°C' },
  { labelKey: '驻波调谐值', field: 'rfTuneValue', unit: '' },
  { labelKey: 'PTT触发电功率', field: 'powerTriggerPttVoltage', unit: 'W' },
];

type ParamTab = 'calibration' | 'other';

export interface EngineeringDashboardModalProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  amplifierProjectData?: AmplifierProjectData;
  amplifierData?: AmplifierData | null;
}

export const EngineeringDashboardModal: React.FC<EngineeringDashboardModalProps> = ({
  open,
  onClose,
  deviceId,
  amplifierProjectData,
  amplifierData,
}) => {
  const { t } = useAmplifierI18n();
  const [formData, setFormData] = useState<AmplifierProjectData>({
    voltageCalibrationCoefficient: 0,
    currentCalibrationCoefficient: 0,
    frequencyMeasurementCalibrationCoefficient: 0,
    vgsCalibrationCoefficient: 0,
    exdacRefvCalibrationMv: 0,
    protectionVoltage: 0,
    protectionCurrent: 0,
    protectionTemperature: 0,
    protectionInputPowerPositive: 0,
    protectionInputPowerNegative: 0,
    protectionOutputPowerPositive: 0,
    protectionOutputPowerNegative: 0,
    protectionRf: 0,
    protectionFrequencyError: 0,
    rfTuneValue: 0,
    powerTriggerPttVoltage: 0,
    inputPowerPositiveCompareVoltage: 0,
    inputPowerNegativeCompareVoltage: 0,
    powerInputPowerPositiveCompareVoltage: 0,
    powerOutputPowerPositiveCompareVoltage: 0,
    outputPowerPositiveCompareVoltage: 0,
    outputPowerNegativeCompareVoltage: 0,
    overCurrentCompareVoltage: 0,
    bandParam: createEmptyBandParam(),
  });
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [activeParamTab, setActiveParamTab] = useState<ParamTab>('calibration');
  const [selectedBandIndex, setSelectedBandIndex] = useState(16);

  useEffect(() => {
    if (amplifierProjectData) {
      setFormData(amplifierProjectData);
    }
  }, [amplifierProjectData]);

  useEffect(() => {
    if (!open) {
      setActiveParamTab('calibration');
      setEditingField(null);
      setSelectedBandIndex(16);
    }
  }, [open]);

  const displayNum = (n: number | undefined | null): string => String(n ?? 0);
  const displayUnitValue = (n: number | undefined | null, unit: string): string =>
    unit ? `${displayNum(n)}${unit}` : displayNum(n);

  const getFormValue = (fieldKey: keyof AmplifierProjectData): number =>
    (formData[fieldKey] as number) ?? 0;

  const getFormValueByKey = (fieldKey: string): number => {
    if (fieldKey.startsWith('bandParam.')) {
      const [, vgsKey, subKey] = fieldKey.split('.');
      const band = formData.bandParam[vgsKey as keyof BandParam];
      return (band?.[subKey as keyof Vgs] as number) ?? 0;
    }
    return (formData as unknown as Record<string, number>)[fieldKey] ?? 0;
  };

  const buildPayloadForField = (fieldKey: string, value: number): Partial<AmplifierProjectData> => {
    if (!fieldKey.startsWith('bandParam.')) {
      return { [fieldKey]: value } as Partial<AmplifierProjectData>;
    }
    const [, vgsKey, subKey] = fieldKey.split('.');
    const band = formData.bandParam[vgsKey as keyof BandParam];
    return {
      bandParam: {
        ...formData.bandParam,
        [vgsKey]: {
          ...band,
          [subKey]: value,
        },
      },
    };
  };

  const handleFieldBlurByKey = (fieldKey: string) => {
    if (editingField !== fieldKey) return;
    const num = parseFloat(editingValue) || 0;
    void handleParameterChange(buildPayloadForField(fieldKey, num));
    setEditingField(null);
  };

  const handleStepperClick = (fieldKey: string, delta: number) => {
    const currentValue = getFormValueByKey(fieldKey);
    void handleParameterChange(buildPayloadForField(fieldKey, currentValue + delta));
  };

  const handleAntennaSelect = async (
    delta: Partial<Pick<AmplifierControlData, 'inputAntenna' | 'outputAntenna'>>,
  ) => {
    try {
      await amplifierMqttService.sendAmplifierControlDelta(deviceId, delta);
    } catch (error) {
      console.error('EngineeringDashboardModal: 天线切换失败:', error);
      message.error(t('天线切换失败'));
    }
  };

  const renderAntennaButton = (
    label: string,
    isActive: boolean,
    onClick: () => void,
    extraClass = '',
  ) => (
    <button
      type="button"
      className={`eng-dash-node eng-dash-node--interactive${
        isActive ? ' eng-dash-node--active' : ''
      }${extraClass ? ` ${extraClass}` : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const handleParameterChange = async (newData: Partial<AmplifierProjectData>) => {
    try {
      await amplifierMqttService.sendProjectParametersDelta(deviceId, newData);
    } catch (error) {
      console.error('EngineeringDashboardModal: 工程参数下发失败:', error);
    }
  };

  const handleFieldBlur = (fieldKey: keyof AmplifierProjectData, editingKey: string) => {
    if (editingField !== editingKey) return;
    const num = parseFloat(editingValue) || 0;
    void handleParameterChange({ [fieldKey]: num });
    setEditingField(null);
  };

  const handleFactoryReset = () => {
    message.info(t('功能待对接'));
  };

  const handleClearAlarm = () => {
    amplifierMqttService.sendClearAlarm(deviceId).then(() => {
      message.success(t('清除报警指令已发送'));
    }).catch((err) => {
      console.error('EngineeringDashboardModal: 清除报警失败:', err);
      message.error(t('清除报警发送失败'));
    });
  };

  const isRemoteControlData = amplifierData?._source === 'remote_control';
  const measurementDivisor = isRemoteControlData ? 1 : 100;
  const swrDivisor = isRemoteControlData ? 1 : 10;

  const displayVoltage = amplifierData?.currentVoltage !== undefined
    ? amplifierData.currentVoltage / measurementDivisor
    : 0;
  const displayCurrent = amplifierData?.currentCurrent !== undefined
    ? amplifierData.currentCurrent / measurementDivisor
    : 0;
  const displayInputSwr = amplifierData?.inputRf !== undefined
    ? amplifierData.inputRf / swrDivisor
    : 1;
  const displayOutputSwr = amplifierData?.outputRf !== undefined
    ? amplifierData.outputRf / swrDivisor
    : 1;

  const inputAntenna = amplifierData?.inputAntenna ?? 0;
  const outputAntenna = amplifierData?.outputAntenna ?? 0;
  const powerOn = amplifierData?.power === 1;
  const antennaConfig = amplifierData?.antennaConfig;

  const getAntennaButtonLabel = (index: number, fallback: string) =>
    resolveAntennaDisplayName(index, antennaConfig, fallback);

  const renderParamInput = (item: ParamFieldItem) => {
    const editingKey = item.fieldKey ?? String(item.field);
    return (
      <div key={editingKey} className="eng-dash-param-row">
        <span className="eng-dash-param-row__label">{t(item.labelKey)}</span>
        <input
          className="eng-dash-param-row__input"
          value={
            editingField === editingKey
              ? editingValue
              : displayUnitValue(getFormValue(item.field), item.unit)
          }
          onFocus={() => {
            setEditingField(editingKey);
            setEditingValue(displayNum(getFormValue(item.field)));
          }}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => handleFieldBlur(item.field, editingKey)}
        />
      </div>
    );
  };

  const renderParamColumn = (items: ParamFieldItem[]) => (
    <div className="eng-dash-params__column">
      {items.map((item) => renderParamInput(item))}
    </div>
  );

  const renderCalibrationStepperRow = (
    label: string,
    fieldKey: string,
    unit: string,
    labelClass = '',
  ) => (
    <div key={fieldKey} className="eng-dash-stepper-row">
      <span className={`eng-dash-stepper-row__label${labelClass ? ` ${labelClass}` : ''}`}>
        {label}
      </span>
      <button
        type="button"
        className="eng-dash-stepper-row__btn"
        onClick={() => handleStepperClick(fieldKey, -1)}
        aria-label={t('减少 {label}', { label })}
      >
        -
      </button>
      <input
        className="eng-dash-stepper-row__value"
        value={
          editingField === fieldKey
            ? editingValue
            : displayUnitValue(getFormValueByKey(fieldKey), unit)
        }
        onFocus={() => {
          setEditingField(fieldKey);
          setEditingValue(displayNum(getFormValueByKey(fieldKey)));
        }}
        onChange={(e) => setEditingValue(e.target.value)}
        onBlur={() => handleFieldBlurByKey(fieldKey)}
      />
      <button
        type="button"
        className="eng-dash-stepper-row__btn"
        onClick={() => handleStepperClick(fieldKey, 1)}
        aria-label={t('增加 {label}', { label })}
      >
        +
      </button>
    </div>
  );

  const selectedBandKey = bandKeyFromIndex(selectedBandIndex);

  const renderCalibrationPanel = () => (
    <div className="eng-dash-params__columns eng-dash-params__columns--calibration">
      <div className="eng-dash-params__column eng-dash-params__column--calibration-left">
        <div className="eng-dash-stepper-row eng-dash-stepper-row--band">
          <span className="eng-dash-stepper-row__label eng-dash-stepper-row__label--wide">{t('频段')}</span>
          <select
            className="eng-dash-stepper-row__select"
            value={selectedBandIndex}
            onChange={(e) => setSelectedBandIndex(Number(e.target.value))}
            aria-label={t('频段')}
          >
            {BAND_OPTIONS.map((band, index) => (
              <option key={band} value={index}>
                {band}
              </option>
            ))}
          </select>
        </div>
        {CALIBRATION_BAND_FIELDS.map((item) =>
          renderCalibrationStepperRow(
            t(item.labelKey),
            `bandParam.${selectedBandKey}.${item.subKey}`,
            item.unit,
            'eng-dash-stepper-row__label--wide',
          ),
        )}
      </div>
      <div className="eng-dash-params__column">
        {CALIBRATION_PARAM_RIGHT.map((item) =>
          renderCalibrationStepperRow(t(item.labelKey), String(item.field), item.unit),
        )}
      </div>
    </div>
  );

  return (
    <AmplifierModalPortal open={open} layer="elevated">
      <div
        className="engineering-dashboard-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <section className="eng-dash-params">
          <div className="eng-dash-params__tabs" role="tablist" aria-label={t('工程参数分页')}>
            <button
              type="button"
              role="tab"
              aria-selected={activeParamTab === 'calibration'}
              className={`eng-dash-params__tab${
                activeParamTab === 'calibration' ? ' eng-dash-params__tab--active' : ''
              }`}
              onClick={() => {
                setActiveParamTab('calibration');
                setEditingField(null);
              }}
            >
              {t('校准系数')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeParamTab === 'other'}
              className={`eng-dash-params__tab${
                activeParamTab === 'other' ? ' eng-dash-params__tab--active' : ''
              }`}
              onClick={() => {
                setActiveParamTab('other');
                setEditingField(null);
              }}
            >
              {t('其他')}
            </button>
          </div>

          <div
            className="eng-dash-params__panel"
            role="tabpanel"
            aria-label={activeParamTab === 'calibration' ? t('校准系数') : t('其他')}
          >
            {activeParamTab === 'calibration' ? (
              renderCalibrationPanel()
            ) : (
              <div className="eng-dash-params__columns">
                {renderParamColumn(OTHER_PARAM_LEFT)}
                {renderParamColumn(OTHER_PARAM_RIGHT)}
              </div>
            )}
          </div>
        </section>

        <section className="eng-dash-actions">
          <button type="button" className="eng-dash-action-btn" onClick={handleFactoryReset}>
            {t('恢复出厂设置')}
          </button>
          <button type="button" className="eng-dash-action-btn" onClick={handleClearAlarm}>
            {t('清除报警')}
          </button>
          <button type="button" className="eng-dash-action-btn" onClick={onClose}>
            Exit
          </button>
        </section>

        <section className="eng-dash-monitor">
          <div className="eng-dash-monitor__log">
            <span className="eng-dash-monitor__log-text">{t('Command send/receive log')}</span>
          </div>

          <div className="eng-dash-monitor__path">
            <div className="eng-dash-path-layout">
              <div className="eng-dash-path-col eng-dash-path-col--in">
                {renderAntennaButton(getAntennaButtonLabel(0, 'IN1'), inputAntenna === 1, () => {
                  void handleAntennaSelect({ inputAntenna: 1 });
                })}
                {renderAntennaButton(getAntennaButtonLabel(1, 'IN2'), inputAntenna === 2, () => {
                  void handleAntennaSelect({ inputAntenna: 2 });
                })}
              </div>

              <span
                className={`eng-dash-node eng-dash-node--pa${
                  powerOn ? ' eng-dash-node--active' : ''
                }`}
              >
                PA
              </span>

              <div className="eng-dash-path-ant-grid">
                {renderAntennaButton(
                  getAntennaButtonLabel(2, 'ANT1'),
                  outputAntenna === 1,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 1 });
                  },
                  'eng-dash-path-ant-grid__ant1',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(3, 'ANT2'),
                  outputAntenna === 2,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 2 });
                  },
                  'eng-dash-path-ant-grid__ant2',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(4, 'ANT3'),
                  outputAntenna === 3,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 3 });
                  },
                  'eng-dash-path-ant-grid__ant3',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(5, 'ANT4'),
                  outputAntenna === 4,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 4 });
                  },
                  'eng-dash-path-ant-grid__ant4',
                )}
              </div>
            </div>
          </div>

          <div className="eng-dash-monitor__electrical">
            <div className="eng-dash-electrical-item">
              <span className="eng-dash-electrical-item__label">{t('电压:')}</span>
              <span className="eng-dash-electrical-item__value">{displayVoltage.toFixed(2)} V</span>
            </div>
            <div className="eng-dash-electrical-item">
              <span className="eng-dash-electrical-item__label">{t('电流:')}</span>
              <span className="eng-dash-electrical-item__value">{displayCurrent.toFixed(2)} A</span>
            </div>
          </div>

          <div className="eng-dash-monitor__power-block">
            <div className="eng-dash-power-block__title">{t('输入 <100W')}</div>
            <div className="eng-dash-power-block__main">
              {amplifierData?.inputPowerPositive !== undefined
                ? `${Math.round(amplifierData.inputPowerPositive)}W`
                : '0W'}
            </div>
            <div className="eng-dash-power-block__sub">
              <span>{t('反向功率')} {amplifierData?.inputPowerNegative ?? 0}W</span>
              <span>{t('驻波')} {displayInputSwr.toFixed(1)}</span>
            </div>
          </div>

          <div className="eng-dash-monitor__power-block">
            <div className="eng-dash-power-block__title">{t('输出')}</div>
            <div className="eng-dash-power-block__main">
              {amplifierData?.outputPowerPositive !== undefined
                ? `${Math.round(amplifierData.outputPowerPositive)}W`
                : '0W'}
            </div>
            <div className="eng-dash-power-block__sub">
              <span>{t('反向功率')} {amplifierData?.outputPowerNegative ?? 0}W</span>
              <span>{t('驻波')} {displayOutputSwr.toFixed(1)}</span>
            </div>
          </div>
        </section>
      </div>
    </AmplifierModalPortal>
  );
};

export default EngineeringDashboardModal;
